// https://github.com/photolane/parabo-next/blob/e52242f29c111d82078a85a1c6dc252fc6e761c0/_templates/product.html
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import queryString from 'utils/queryString';
import productStillImages from 'utils/productStillImages';
import { Alert, Button, Grid, Typography } from 'components/base';
import {
  HtmlContent,
  ProductDetailCarousel,
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  ScriptMarkupProduct,
  faqContentToItems,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import ProductPrice from '../../components/custom/productPrice';
import useCartContext from 'ecommerce/context/cart';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponPricePreview } from 'ecommerce/lib/coupon';
import ProductConfiguration, { prepareBundleData } from 'ecommerce/components/productConfiguration';
import { getInitialPriceData } from 'ecommerce/lib/productConfiguration';
import useEditorContext from 'editor/context/editorContext';

import { EditorPageLink, CartPageLink } from '../../components/pageLinks';
import ProductSections from '../../components/product/productSections';
import { Head, Layout } from '../../components/layout';
import SETTINGS from '../../settings';

// Card type in the <title> after the design name. The family captions differ
// ("Gold Foil Photo Card") and would give dozens of designs the same title.
const CARD_TYPE_TITLES = {
  'photo-card': 'Holiday Photo Card',
  'photo-card-foil': 'Gold Foil Holiday Photo Card',
  'photo-greeting-card': 'Photo Greeting Card',
  'photo-greeting-card-foil': 'Gold Foil Photo Greeting Card',
};

/**
 * Product has many skuProducts. In case of bundle, there is also secondaryProduct.
 * By default is secondaryProductIndex as -1 - that means "Print Only" version
 */
const CardDetailPage = props => {
  const cartContext = useCartContext();
  const claimedCoupon = useClaimedCoupon();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();

  const product = useMemo(() => {
    const value = dataContext.getProduct(props.productId);
    return {
      ...value,
      options: [props.photoCard],
      images: [
        { key: `sku/${props.photoCard.id}` },
        // Preserve `type` so a future video slot still drives the carousel's video branch.
        ...value.images.map(({ key, type }) => ({ key: `products/${key}`, type })),
      ],
    };
  }, [props.productId, props.photoCard]);
  const defaultPrintOnly = product.config.defaultPrintOnly ?? false;
  const [skuProduct, setSkuProduct] = useState(props.photoCard);

  /**
   * Configuration { product, primaryProductData, secondaryProductData, price, bundle }
   */
  const [configuration, setConfiguration] = useState(() =>
    getInitialPriceData(product, dataContext, defaultPrintOnly),
  );

  // Reset during render: a reset effect would run after the picker's mount onChange and wipe it
  const [shownProduct, setShownProduct] = useState(product);
  if (shownProduct !== product) {
    setShownProduct(product);
    setSkuProduct(props.photoCard);
    setConfiguration(getInitialPriceData(product, dataContext, defaultPrintOnly));
  }

  // The card's price is per card times the tier quantity, so the coupon previews on that total.
  const { line: couponLine, price: couponPrice } = couponPricePreview(
    claimedCoupon.previewSummary,
    configuration,
    configuration.quantity ?? 1,
  );

  const handleOnAddToCartClick = () => {
    cartContext.addItem(
      configuration.product,
      configuration.product.version,
      configuration.primaryProductData,
      false,
      prepareBundleData(dataContext, configuration),
    );
  };

  const handleOnStartEditorClick = () => {
    if (configuration.bundle || configuration.upsell) {
      editorContext.createItem(
        configuration.product.id,
        configuration.product.version,
        prepareBundleData(dataContext, configuration),
      );
    } else {
      editorContext.removeBundle(configuration.product.id);
    }
  };

  const handleOnChangeConfiguration = data => {
    // if (data.product) {
    //   setSkuProduct(data.product);
    // }
    setConfiguration(data);
  };

  // Shared by the top section and the sticky bar of the redesigned layout.
  const renderBuyAction = () => {
    if (!skuProduct) return null;

    if (skuProduct.display) {
      return (
        <CartPageLink>
          <Button type="primary" block onClick={handleOnAddToCartClick}>
            Add to Cart
          </Button>
        </CartPageLink>
      );
    }

    if (product.config.continueButtonUrl) {
      return (
        <Link
          href={queryString.addParams(product.config.continueButtonUrl, {
            product: product.id,
            ...configuration.config,
          })}
          passHref
          legacyBehavior
        >
          <Button type="primary" block onClick={handleOnStartEditorClick}>
            Continue
          </Button>
        </Link>
      );
    }

    return (
      <EditorPageLink
        productId={skuProduct.id}
        version={skuProduct.version}
        quantity={configuration.quantity}
      >
        <Button type="primary" block onClick={handleOnStartEditorClick}>
          Continue
        </Button>
      </EditorPageLink>
    );
  };
  const buyAction = renderBuyAction();

  // The card's price is per card, the shown price is the whole tier.
  const setPrice = configuration.price * (configuration.quantity ?? 1);

  const markupImages = productStillImages(product).map(image =>
    getCloudinaryUrl({ imageKey: image.key, prefix: '', type: 'big', format: 'jpg' }),
  );

  const topSection = (
    <Grid.Row>
      <Grid.Col xs={12} lg={7} className="pr-lg-2">
        <ProductDetailCarousel prefix="" images={product.images} rounded />
      </Grid.Col>

      <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-data">
        <Typography.H1 className="mb-3">{skuProduct.name}</Typography.H1>

        <div className="list-styled mb-4">
          <HtmlContent html={props.content.description} />
        </div>

        {skuProduct ? (
          <>
            <div>
              <ProductConfiguration
                key={props.photoCard.id}
                product={product}
                skuProduct={skuProduct}
                onChange={handleOnChangeConfiguration}
                hidden={product.config.hideProductConfiguration}
                defaultPrintOnly={defaultPrintOnly}
                hidePrintOnly={product.config.hidePrintOnly ?? false}
                showSkuDescription={product.config.showSkuDescription}
                labels={product.config.labels}
                minNumberOfItems={1}
                productType="Card"
              />
              {/* Price label and value are UI text, not content headings — divs keep the heading styling */}
              <div className="h6">
                PRICE{product.config.showSkuPriceStartingAt && ' STARTING AT'}
              </div>
              <ProductPrice
                price={setPrice}
                discountedPrice={couponPrice}
                currency={product.currency}
                couponLine={couponLine}
              />
            </div>
            {buyAction}
          </>
        ) : (
          <Alert type="danger" className="text-center">
            Sorry, this product is not available.
          </Alert>
        )}
      </Grid.Col>
    </Grid.Row>
  );

  return (
    <React.Fragment key={product.id}>
      <Head
        title={`${props.photoCard.name} – ${CARD_TYPE_TITLES[props.productId]}`}
        description={props.content.seoDescription ?? props.content.shortDescription}
        image={props.content.seoUrl}
      />

      <ScriptMarkupProduct
        id={props.photoCard.id}
        url={`${SETTINGS.HOST}/${props.photoCard.id}`}
        name={props.photoCard.name}
        description={props.content.shortDescription}
        images={markupImages}
        // One Offer for the default set (merchant listings need Offer, not AggregateOffer).
        prices={[setPrice]}
        currency={product.currency}
        brand="Parabo Press"
        host={SETTINGS.HOST}
      />

      {props.content.faq && <ScriptMarkupFaq items={faqContentToItems(props.content.faq)} />}

      {props.breadcrumb && <ScriptMarkupBreadcrumb items={props.breadcrumb} host={SETTINGS.HOST} />}

      <Layout>
        <ProductSections
          product={product}
          content={props.content}
          breadcrumb={props.breadcrumb}
          buyAction={buyAction}
          summary={(configuration.bundle ?? configuration.product)?.name}
          price={
            <ProductPrice
              price={setPrice}
              discountedPrice={couponPrice}
              currency={product.currency}
            />
          }
        >
          {topSection}
        </ProductSections>
      </Layout>
    </React.Fragment>
  );
};

CardDetailPage.getInitialProps = async () => {
  throw new Error('Cannot access this page directly. See /[...params] page');
};

CardDetailPage.propTypes = {
  productId: PropTypes.string.isRequired,
  photoCard: PropTypes.object,
  content: PropTypes.object,
  // Home > category > card design, shared with the BreadcrumbList JSON-LD.
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
};

export default CardDetailPage;
